import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { catalogApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  ArrowLeftIcon,
  GlobeAltIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  SparklesIcon,
  StarIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

interface Category {
  categoryId: string;
  name: string;
  slug: string;
}

interface Campaign {
  name: string;
  description?: string;
  type: 'percentage' | 'flat' | 'tiered';
  commissionValue: number;
  cookieDuration: number;
}

interface ListingDetail {
  listingId: string;
  organizationId: string;
  organizationName: string;
  headline: string;
  description: string;
  logo?: string;
  websiteUrl?: string;
  categories: Category[];
  commissionHighlight: string;
  cookieDuration: number;
  averageOrderValue?: number;
  conversionRate?: number;
  benefits: string[];
  applicationMode: 'auto_approve' | 'manual_review';
  applicationRequirements?: string;
  minAudienceSize?: number;
  allowedCountries?: string[];
  tier: 'basic' | 'featured' | 'spotlight';
  badges: string[];
  stats: {
    activeAffiliates: number;
  };
  createdAt: string;
}

export default function CatalogListing() {
  const { listingId } = useParams<{ listingId: string }>();
  const { isAuthenticated, user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState<ListingDetail | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    applicantName: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '',
    applicantEmail: user?.email || '',
    audienceDescription: '',
    websiteUrl: '',
    socialLinks: [''],
    monthlyVisitors: '',
    promotionPlan: '',
  });

  const getDashboardLink = () => {
    if (!isAuthenticated) return '/login';
    if (user?.role === 'superadmin') return '/admin';
    if (user?.role === 'org_admin') return '/org';
    return '/dashboard';
  };

  useEffect(() => {
    if (listingId) {
      loadListing();
    }
  }, [listingId]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        applicantName: user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : prev.applicantName,
        applicantEmail: user.email || prev.applicantEmail,
      }));
    }
  }, [user]);

  const loadListing = async () => {
    try {
      const response = await catalogApi.getListing(listingId!);
      if (response.data.success) {
        setListing(response.data.data.listing);
        setCampaigns(response.data.data.campaigns || []);
      }
    } catch (error) {
      console.error('Failed to load listing:', error);
      toast.error('Failed to load listing');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await catalogApi.apply(listingId!, {
        applicantName: formData.applicantName,
        applicantEmail: formData.applicantEmail,
        audienceDescription: formData.audienceDescription,
        websiteUrl: formData.websiteUrl || undefined,
        socialLinks: formData.socialLinks.filter(l => l.trim() !== ''),
        monthlyVisitors: formData.monthlyVisitors ? parseInt(formData.monthlyVisitors, 10) : undefined,
        promotionPlan: formData.promotionPlan || undefined,
      });

      if (response.data.success) {
        setApplicationSubmitted(true);
        toast.success(response.data.data.message);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addSocialLink = () => {
    if (formData.socialLinks.length < 5) {
      setFormData({ ...formData, socialLinks: [...formData.socialLinks, ''] });
    }
  };

  const updateSocialLink = (index: number, value: string) => {
    const newLinks = [...formData.socialLinks];
    newLinks[index] = value;
    setFormData({ ...formData, socialLinks: newLinks });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-charcoal mb-4">Listing not found</h2>
          <Link to="/catalog" className="inline-block bg-rust text-warm-white px-6 py-3 rounded-lg font-medium hover:bg-rust/90 transition-colors">
            Back to Catalog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="bg-charcoal px-6 lg:px-16 py-6 flex justify-between items-center">
        <Link to="/" className="font-display text-2xl font-bold text-warm-white tracking-tight">
          venndela
        </Link>
        <ul className="hidden md:flex items-center gap-12">
          <li>
            <Link to="/" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Home
            </Link>
          </li>
          <li>
            <Link to="/catalog" className="text-rust text-sm font-medium">
              Catalog
            </Link>
          </li>
          <li>
            <Link to="/pricing" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Pricing
            </Link>
          </li>
          {isAuthenticated ? (
            <li>
              <Link to={getDashboardLink()} className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Dashboard
              </Link>
            </li>
          ) : (
            <li>
              <Link to="/login" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Log In
              </Link>
            </li>
          )}
        </ul>
      </nav>

      {/* Header */}
      <div className="bg-warm-white border-b border-tan/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/catalog" className="inline-flex items-center text-charcoal/60 hover:text-charcoal mb-6 transition-colors">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Catalog
          </Link>

          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Logo */}
            {listing.logo ? (
              <img src={listing.logo} alt="" className="w-24 h-24 rounded-xl object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-cream flex items-center justify-center">
                <span className="text-4xl font-serif font-bold text-charcoal/40">
                  {listing.organizationName.charAt(0)}
                </span>
              </div>
            )}

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-serif text-3xl text-charcoal">{listing.organizationName}</h1>
                {listing.badges.map((badge) => (
                  <span
                    key={badge}
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      badge === 'Spotlight' || badge === 'Top Pick'
                        ? 'bg-rust/10 text-rust'
                        : 'bg-sage/10 text-sage'
                    }`}
                  >
                    {listing.tier === 'spotlight' && <SparklesIcon className="w-3 h-3 inline mr-1" />}
                    {listing.tier === 'featured' && <StarIcon className="w-3 h-3 inline mr-1" />}
                    {badge}
                  </span>
                ))}
              </div>

              <p className="text-lg text-charcoal/70 mb-4">{listing.headline}</p>

              <div className="flex flex-wrap gap-4 text-sm">
                {listing.websiteUrl && (
                  <a
                    href={listing.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-rust hover:underline"
                  >
                    <GlobeAltIcon className="w-4 h-4" />
                    {new URL(listing.websiteUrl).hostname}
                  </a>
                )}
                <span className="flex items-center gap-1 text-charcoal/60">
                  <ClockIcon className="w-4 h-4" />
                  {listing.cookieDuration} day cookie
                </span>
                <span className="flex items-center gap-1 text-charcoal/60">
                  <UserGroupIcon className="w-4 h-4" />
                  {listing.stats.activeAffiliates} active affiliates
                </span>
              </div>
            </div>

            {/* Commission Highlight */}
            <div className="bg-sage/10 border border-sage/30 rounded-xl p-6 text-center min-w-[200px]">
              <p className="text-sm text-sage mb-1">Commission</p>
              <p className="text-2xl font-serif font-bold text-sage">{listing.commissionHighlight}</p>
              {listing.applicationMode === 'auto_approve' && (
                <p className="text-xs text-sage mt-2 flex items-center justify-center gap-1">
                  <SparklesIcon className="w-3.5 h-3.5" />
                  Instant approval
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {listing.categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/catalog?category=${cat.slug}`}
                  className="px-3 py-1 bg-cream text-charcoal/70 text-sm rounded-full hover:bg-tan/30 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Description */}
            <div className="bg-warm-white rounded-2xl shadow-sm border border-tan/20 overflow-hidden">
              <div className="px-6 py-4 border-b border-tan/20">
                <h2 className="font-display text-lg font-semibold text-charcoal">About the Program</h2>
              </div>
              <div className="p-6">
                <p className="text-charcoal/70 whitespace-pre-wrap leading-relaxed">{listing.description}</p>
              </div>
            </div>

            {/* Benefits */}
            {listing.benefits.length > 0 && (
              <div className="bg-warm-white rounded-2xl shadow-sm border border-tan/20 overflow-hidden">
                <div className="px-6 py-4 border-b border-tan/20">
                  <h2 className="font-display text-lg font-semibold text-charcoal">Why Join?</h2>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {listing.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                        <span className="text-charcoal/70">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Commission Structure */}
            {campaigns.length > 0 && (
              <div className="bg-warm-white rounded-2xl shadow-sm border border-tan/20 overflow-hidden">
                <div className="px-6 py-4 border-b border-tan/20">
                  <h2 className="font-display text-lg font-semibold text-charcoal">Commission Structure</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {campaigns.map((campaign, i) => (
                      <div key={i} className="p-4 bg-cream rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-charcoal">{campaign.name}</h4>
                            {campaign.description && (
                              <p className="text-sm text-charcoal/60 mt-1">{campaign.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-serif font-bold text-rust">
                              {campaign.type === 'percentage'
                                ? `${campaign.commissionValue}%`
                                : `$${(campaign.commissionValue / 100).toFixed(2)}`}
                            </p>
                            <p className="text-xs text-charcoal/50">{campaign.cookieDuration} day cookie</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Requirements */}
            {listing.applicationRequirements && (
              <div className="bg-warm-white rounded-2xl shadow-sm border border-tan/20 overflow-hidden">
                <div className="px-6 py-4 border-b border-tan/20">
                  <h2 className="font-display text-lg font-semibold text-charcoal">Requirements</h2>
                </div>
                <div className="p-6">
                  <p className="text-charcoal/70 whitespace-pre-wrap leading-relaxed">{listing.applicationRequirements}</p>
                  {listing.minAudienceSize && (
                    <p className="text-sm text-charcoal/50 mt-4">
                      Minimum monthly visitors: {listing.minAudienceSize.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Application Form */}
          <div className="lg:col-span-1">
            <div className="bg-warm-white rounded-2xl shadow-sm border border-tan/20 overflow-hidden sticky top-4">
              <div className="px-6 py-4 border-b border-tan/20">
                <h2 className="font-display text-lg font-semibold text-charcoal">Apply to Join</h2>
              </div>
              <div className="p-6">
                {applicationSubmitted ? (
                  <div className="text-center py-6">
                    <CheckCircleIcon className="w-12 h-12 text-sage mx-auto mb-4" />
                    <h3 className="font-display text-lg font-medium text-charcoal mb-2">Application Submitted!</h3>
                    <p className="text-charcoal/60 text-sm">
                      {listing.applicationMode === 'auto_approve'
                        ? 'Your application has been approved! Check your email for next steps.'
                        : 'We\'ll review your application and get back to you soon.'}
                    </p>
                  </div>
                ) : showApplicationForm ? (
                  <form onSubmit={handleSubmitApplication} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1">Your Name *</label>
                      <input
                        type="text"
                        value={formData.applicantName}
                        onChange={(e) => setFormData({ ...formData, applicantName: e.target.value })}
                        className="w-full px-3 py-2 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1">Email *</label>
                      <input
                        type="email"
                        value={formData.applicantEmail}
                        onChange={(e) => setFormData({ ...formData, applicantEmail: e.target.value })}
                        className="w-full px-3 py-2 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1">Describe Your Audience *</label>
                      <textarea
                        value={formData.audienceDescription}
                        onChange={(e) => setFormData({ ...formData, audienceDescription: e.target.value })}
                        className="w-full px-3 py-2 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust min-h-[100px]"
                        placeholder="Tell us about your audience, niche, and reach..."
                        required
                        minLength={20}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1">Website URL</label>
                      <input
                        type="url"
                        value={formData.websiteUrl}
                        onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                        className="w-full px-3 py-2 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust"
                        placeholder="https://"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1">Social Links</label>
                      {formData.socialLinks.map((link, i) => (
                        <input
                          key={i}
                          type="url"
                          value={link}
                          onChange={(e) => updateSocialLink(i, e.target.value)}
                          className="w-full px-3 py-2 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust mb-2"
                          placeholder="https://..."
                        />
                      ))}
                      {formData.socialLinks.length < 5 && (
                        <button
                          type="button"
                          onClick={addSocialLink}
                          className="text-sm text-rust hover:underline"
                        >
                          + Add another link
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1">Monthly Visitors</label>
                      <input
                        type="number"
                        value={formData.monthlyVisitors}
                        onChange={(e) => setFormData({ ...formData, monthlyVisitors: e.target.value })}
                        className="w-full px-3 py-2 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust"
                        placeholder="Approximate monthly visitors"
                        min={0}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal mb-1">How will you promote?</label>
                      <textarea
                        value={formData.promotionPlan}
                        onChange={(e) => setFormData({ ...formData, promotionPlan: e.target.value })}
                        className="w-full px-3 py-2 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust min-h-[80px]"
                        placeholder="Describe your promotion strategy..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-rust text-warm-white py-3 rounded-lg font-medium hover:bg-rust/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowApplicationForm(false)}
                      className="w-full bg-cream border border-tan/30 text-charcoal py-3 rounded-lg font-medium hover:bg-tan/20 transition-colors"
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-cream rounded-lg">
                      <CurrencyDollarIcon className="w-5 h-5 text-rust" />
                      <div>
                        <p className="text-sm font-medium text-charcoal">{listing.commissionHighlight}</p>
                        <p className="text-xs text-charcoal/50">Per sale</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-cream rounded-lg">
                      <ClockIcon className="w-5 h-5 text-sage" />
                      <div>
                        <p className="text-sm font-medium text-charcoal">{listing.cookieDuration} days</p>
                        <p className="text-xs text-charcoal/50">Cookie duration</p>
                      </div>
                    </div>

                    {listing.applicationMode === 'auto_approve' && (
                      <div className="flex items-center gap-3 p-3 bg-sage/10 rounded-lg border border-sage/30">
                        <SparklesIcon className="w-5 h-5 text-sage" />
                        <div>
                          <p className="text-sm font-medium text-sage">Instant Approval</p>
                          <p className="text-xs text-sage/70">Start earning immediately</p>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => setShowApplicationForm(true)}
                      className="w-full bg-rust text-warm-white py-3 rounded-lg font-medium hover:bg-rust/90 transition-colors"
                    >
                      Apply Now
                    </button>

                    <p className="text-xs text-center text-charcoal/50">
                      Free to join. No credit card required.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-white/10 py-12 px-6 lg:px-16 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-display text-xl font-bold text-warm-white">
            venndela
          </Link>

          <ul className="flex flex-wrap justify-center gap-8">
            <li>
              <Link to="/" className="text-tan text-sm hover:text-warm-white transition-colors">
                Home
              </Link>
            </li>
            <li>
              <Link to="/catalog" className="text-tan text-sm hover:text-warm-white transition-colors">
                Catalog
              </Link>
            </li>
            <li>
              <Link to="/pricing" className="text-tan text-sm hover:text-warm-white transition-colors">
                Pricing
              </Link>
            </li>
            <li>
              <a href="#" className="text-tan text-sm hover:text-warm-white transition-colors">
                Docs
              </a>
            </li>
            <li>
              <a href="mailto:support@venndela.com" className="text-tan text-sm hover:text-warm-white transition-colors">
                Contact
              </a>
            </li>
          </ul>

          <p className="text-charcoal/50 text-sm">
            © {new Date().getFullYear()} Venndela. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
